import { useState } from 'react';
import { format } from 'date-fns';
import { BellIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAccessControl } from '@/contexts/AccessContext';
import {
  useGetNotificationsQuery,
  useGetUnreadNotificationsCountQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from '@/services/notifications/notificationsApi';
import type { INotification } from '@/services/notifications/interfaces';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 60;

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const { profile } = useAccessControl();

  const { data: unreadCount = 0 } = useGetUnreadNotificationsCountQuery();
  const {
    data: notificationsData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetNotificationsQuery(
    { limit: PAGE_SIZE, cursor },
    { skip: !open, refetchOnMountOrArgChange: true }
  );
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllNotificationsAsRead, { isLoading: isMarkingAllRead }] =
    useMarkAllNotificationsAsReadMutation();

  const notifications = notificationsData?.items ?? [];
  const recipientName = [profile?.user?.firstName, profile?.user?.lastName]
    .filter(Boolean)
    .join(' ');

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setCursor(undefined);
  };

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (isFetching || isError) return;
    const el = event.currentTarget;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_LOAD_THRESHOLD_PX;
    const { hasMore, nextCursor } = notificationsData ?? {};
    if (nearBottom && hasMore && nextCursor) setCursor(nextCursor);
  };

  const handleNotificationClick = (notification: INotification) => {
    if (!notification.isRead) markNotificationAsRead({ id: notification.id });
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          title="Notifications"
          className="relative flex size-8 shrink-0 items-center justify-center rounded-full text-fg-3 transition-colors hover:bg-m4-graphite-50 hover:text-fg-1"
        >
          <BellIcon className="size-5" />
          {unreadCount > 0 && (
            <span className="absolute top-0.5 right-0.5 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border-[1.5px] border-white bg-danger px-1 text-[9px] font-bold leading-none text-white">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 gap-0">
        <div className="flex items-center justify-between px-4 py-3">
          <h2 className="text-base font-semibold text-m4-heading">Notifications</h2>
          <button
            type="button"
            disabled={unreadCount === 0 || isMarkingAllRead}
            onClick={() => markAllNotificationsAsRead()}
            className="text-sm font-medium text-m4-purple-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:no-underline"
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto px-2 pb-2" onScroll={handleScroll}>
          {isLoading && notifications.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-slate-500">Loading notifications…</p>
          ) : isError && notifications.length === 0 ? (
            <div className="px-2 py-6 text-center">
              <p className="text-sm text-slate-500">Unable to load notifications.</p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-1 text-sm font-medium text-m4-purple-700 hover:underline"
              >
                Try again
              </button>
            </div>
          ) : notifications.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-slate-500">No notifications yet</p>
          ) : (
            <>
              {notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    'mb-2 w-full rounded-lg p-3 text-left last:mb-0',
                    notification.isRead
                      ? 'bg-white hover:bg-slate-50'
                      : 'bg-m4-purple-50 hover:bg-m4-purple-100 cursor-pointer'
                  )}
                >
                  <p className="text-sm font-semibold text-m4-heading">{notification.heading}</p>
                  <p className="mt-0.5 text-sm text-slate-600">{notification.body}</p>
                  <p className="mt-1 text-xs text-slate-400">
                    To: {recipientName || '—'} ·{' '}
                    {format(new Date(notification.createdAt), 'hh:mm aaa')}
                  </p>
                </button>
              ))}
              {isFetching && cursor && (
                <p className="py-2 text-center text-xs text-slate-400">Loading more…</p>
              )}
              {isError && !isFetching && (
                <div className="py-2 text-center">
                  <span className="text-xs text-slate-500">Unable to load more notifications.</span>{' '}
                  <button
                    type="button"
                    onClick={() => refetch()}
                    className="text-xs font-medium text-m4-purple-700 hover:underline"
                  >
                    Retry
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
